import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { PalangDaftar, Paginasi } from "../../components/etams/PalangDaftar";
import { useDaftar } from "../../hooks/useDaftar";
import { useAsetMeta, label, tone, labelPengingatLaporan } from "../../hooks/useAsetMeta";
import { listStatusLaporan, apiErrorMessage } from "../../lib/asetApi";
import { ASET_NAV_ROLES } from "../../lib/roles";
import { formatDate } from "../../lib/format";
import { ClipboardCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import LaporanFormDialog from "./LaporanFormDialog";
import { useRegions, useNamaKaryawan, namaKaryawan, periodeLabel } from "./bantuAset";

// Laporan Bulanan Aset (L3c, 4b-3) — baris view v_aset_laporan_bulanan_status
// (GET /api/aset/laporan/status; KC/ADMIN dijepit region backend, OWNER pilih
// region atau semua). R8: tiap aset wajib lapor punya baris bulan BERJALAN
// selalu + bulan LALU bila belum lapor; aset keluar (DIJUAL/HILANG/DIHAPUSKAN)
// nol punya baris — jadi nol punya tombol Lapor.
//
// Bawaan: yang BELUM lapor (BELUM_LAPOR, H3, NORMAL), urut BELUM_LAPOR (bulan
// lalu) → H3 → NORMAL, lalu kode aset. Toggle "tampilkan yang sudah lapor"
// menambahkan SUDAH_LAPOR di bawah. Form cepat = dialog kecil per baris
// (`LaporanFormDialog`) — periode dari baris, pelapor = PIC aset.
//
// L4–L6/S1: label + rona dari meta; H3 berlabel "Batas Lapor {n} Hari Lagi"
// lewat `labelPengingatLaporan`. Tombol "Lapor" = ASET_NAV_ROLES (cermin
// PERAN_CATAT); backend `wajib_catat` tetap pagarnya.

const SEMUA = "__semua__";
const URUT = { BELUM_LAPOR: 0, H3: 1, NORMAL: 2, SUDAH_LAPOR: 3 };

export function urutLaporan(a, b) {
  const ua = URUT[a.status_pengingat] ?? 9;
  const ub = URUT[b.status_pengingat] ?? 9;
  if (ua !== ub) return ua - ub;
  if (a.periode !== b.periode) return a.periode < b.periode ? -1 : 1;
  return String(a.aset_kode).localeCompare(String(b.aset_kode), "id", { numeric: true });
}

export default function LaporanBulananAset() {
  const { roleCode, isOwner } = useAuth();
  const canLapor = ASET_NAV_ROLES.includes(roleCode);
  const { meta } = useAsetMeta();
  const [items, setItems] = useState([]);
  const [memuat, setMemuat] = useState(true);
  const [regionFilter, setRegionFilter] = useState("");
  const [tampilSudah, setTampilSudah] = useState(false);
  const [baris, setBaris] = useState(null);
  const regions = useRegions(isOwner);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const p = {};
      if (isOwner && regionFilter) p.region_id = regionFilter;
      const r = await listStatusLaporan(p);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat status laporan bulanan."));
      setItems([]);
    } finally {
      setMemuat(false);
    }
  }, [isOwner, regionFilter]);
  useEffect(() => { muat(); }, [muat]);

  const tersaring = useMemo(() => {
    const b = tampilSudah ? items : items.filter((x) => x.status_pengingat !== "SUDAH_LAPOR");
    return [...b].sort(urutLaporan);
  }, [items, tampilSudah]);
  const daftar = useDaftar(tersaring, { kunciCari: ["aset_kode", "aset_nama"], urutAwal: null });
  // Nama PIC hanya untuk baris yang tampil (satu panggilan `?ids=`) — pola daftar seragam 28 Sep 2026.
  const semuaKaryawan = useNamaKaryawan([...daftar.tampil.map((s) => s.pic_employee_id), baris?.pic_employee_id]);
  const belum = items.filter((x) => x.status_pengingat !== "SUDAH_LAPOR").length;

  return (
    <div className="space-y-6" data-testid="laporan-bulanan-aset-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" /> Laporan Bulanan Aset
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kewajiban lapor tiap aset untuk bulan berjalan dan bulan lalu yang belum dilaporkan.
            {belum > 0 ? ` ${belum} belum lapor.` : items.length > 0 ? " Semua sudah lapor." : ""}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={muat} data-testid="laporan-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari kode / nama aset…" testId="laporan-palang">
          {isOwner && regions.length > 0 && (
            <Select value={regionFilter || SEMUA} onValueChange={(v) => setRegionFilter(v === SEMUA ? "" : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="laporan-filter-region"><SelectValue placeholder="Wilayah" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SEMUA}>Semua Wilayah</SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={tampilSudah} onChange={(e) => setTampilSudah(e.target.checked)} data-testid="laporan-toggle-sudah" />
            Tampilkan yang sudah lapor
          </label>
        </PalangDaftar>

        {memuat ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="laporan-memuat">Memuat...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Aset</th>
                  <th className="text-left px-3 py-2">Periode</th>
                  <th className="text-left px-3 py-2">Status Laporan</th>
                  <th className="text-left px-3 py-2">PIC</th>
                  {canLapor && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && (
                  <tr><td colSpan={canLapor ? 5 : 4} className="px-3 py-8 text-center text-muted-foreground" data-testid="laporan-kosong">
                    {daftar.cari.trim()
                      ? "Tidak ada aset yang cocok."
                      : items.length === 0
                        ? "Belum ada aset yang wajib lapor di cabang ini."
                        : "Semua aset sudah dilaporkan bulan ini."}
                  </td></tr>
                )}
                {daftar.tampil.map((s) => (
                  <tr key={`${s.aset_id}-${s.periode}`} className="border-t border-border hover:bg-muted/50" data-testid={`laporan-row-${s.aset_id}-${String(s.periode).slice(0, 7)}`}>
                    <td className="px-3 py-2">
                      <Link to={`/aset/${s.aset_id}`} className="font-mono text-xs underline underline-offset-2 hover:text-foreground">{s.aset_kode}</Link>
                      <span className="block">{s.aset_nama}</span>
                      <span className="block text-xs text-muted-foreground">{label(meta, "kategori", s.aset_kategori)}</span>
                    </td>
                    <td className="px-3 py-2 font-medium">{periodeLabel(s.periode)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={tone(meta, "status_pengingat_laporan", s.status_pengingat)} testId={`laporan-status-${s.aset_id}-${String(s.periode).slice(0, 7)}`}>
                        {labelPengingatLaporan(meta, s)}
                      </StatusBadge>
                      {s.tanggal_lapor ? (
                        <span className="block text-xs text-muted-foreground mt-1">
                          dilaporkan {formatDate(s.tanggal_lapor)} · {label(meta, "status_laporan", s.status_laporan)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs">{namaKaryawan(semuaKaryawan, s.pic_employee_id)}</td>
                    {canLapor && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {!s.sudah_lapor && (
                          <Button size="sm" onClick={() => setBaris(s)} data-testid={`laporan-lapor-${s.aset_id}-${String(s.periode).slice(0, 7)}`}>Lapor</Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="laporan-paginasi" />
          </div>
        )}
      </Card>

      <LaporanFormDialog
        terbuka={!!baris}
        onTutup={() => setBaris(null)}
        onSelesai={() => { setBaris(null); muat(); }}
        meta={meta}
        baris={baris}
        namaPic={baris ? namaKaryawan(semuaKaryawan, baris.pic_employee_id) : null}
      />
    </div>
  );
}
